import { useState, useEffect, useRef } from "react";
import { ConfirmActionModal } from "../modals/ConfirmActionModal";
import { AiMealFillModal } from "../modals/AiMealFillModal";
import { mapAiDraftToForm } from "../../utils/mapAiDraftToForm";
import { UserMealFormHeader } from "../EditMealPage/UserMealFormHeader";
import { UserMealFormMealName } from "../EditMealPage/UserMealFormMealName";
import { UserMealFormMealCategory } from "../EditMealPage/UserMealFormMealCategory";
import { UserMealFormMealType } from "../EditMealPage/UserMealFormMealType";
import { UserMealFormDuration } from "../EditMealPage/UserMealFormDuration";
import { UserMealFormServings } from "../EditMealPage/UserMealFormServings";
import { UserMealFormIngredients } from "../EditMealPage/UserMealFormIngredients";
import { UserMealFormFooter } from "../EditMealPage/UserMealFormFooter";
import { UserMealFormTabs } from "../EditMealPage/UserMealFormTabs";

const createBlankForm = (ingredientUnits) => ({
  mealName: "",
  mealCategories: {
    breakfast: false,
    lunch: false,
    dinner: false,
    snack: false,
  },
  mealTagList: [],
  timeMins: "",
  servings: "",
  ingredients: [
    {
      id: crypto.randomUUID(),
      name: "",
      quantity: "",
      unit: ingredientUnits?.[0] || "",
    },
  ],
});

export function UserMealForm({
  mealData,
  userMealTags,
  ingredientUnits,
  onSubmit,
  onCancel,
  onAiParse,
  heading,
  confirmButtonText,
}) {
  const [form, setForm] = useState(
    () => mealData || createBlankForm(ingredientUnits),
  );
  const originalForm = mealData || createBlankForm(ingredientUnits);

  const [showConfirmCancelModal, setShowConfirmCancelModal] = useState(false);

  const confirmCancelClicked = (value) => {
    if (isFormDirty(form, originalForm)) {
      setShowConfirmCancelModal(value);
    } else {
      onCancel();
    }
  };

  /**
   * Structural deep comparison helper that ignores randomized dynamic IDs
   */
  const isFormDirty = (currentForm, originalForm) => {
    // 1. Quick reference check
    if (currentForm === originalForm) return false;

    // 2. Normalize and strip dynamic properties that shouldn't flag the form as dirty
    const normalizeIngredients = (ingredients) =>
      ingredients.map(({ id, ...rest }) => ({
        ...rest,
        name: rest.name?.trim() || "",
        quantity: rest.quantity?.toString().trim() || "",
        unit: rest.unit || "",
      }));

    // 3. Build sanitized payloads for clean object matching
    const sortedTagIds = (tags) =>
      [...tags].map((tag) => tag.tagId).sort((a, b) => a - b);

    const currentPayload = {
      ...currentForm,
      mealName: currentForm.mealName?.trim() || "",
      timeMins: currentForm.timeMins?.toString().trim() || "",
      servings: currentForm.servings?.toString().trim() || "",
      mealTagList: sortedTagIds(currentForm.mealTagList),
      ingredients: normalizeIngredients(currentForm.ingredients),
    };

    const originalPayload = {
      ...originalForm,
      mealName: originalForm.mealName?.trim() || "",
      timeMins: originalForm.timeMins?.toString().trim() || "",
      servings: originalForm.servings?.toString().trim() || "",
      mealTagList: sortedTagIds(originalForm.mealTagList),
      ingredients: normalizeIngredients(originalForm.ingredients),
    };

    // 4. Clean structural comparison
    return JSON.stringify(currentPayload) !== JSON.stringify(originalPayload);
  };

  useEffect(() => {
    if (mealData) {
      setForm(mealData);
    }
  }, [mealData]);

  const [showMealTypeDropdown, setShowMealTypeDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [showAiFillModal, setShowAiFillModal] = useState(false);
  const formRef = useRef(null);

  const handleAiGenerate = async ({ mode, value }) => {
    const draft = await onAiParse(mode, value);
    setForm(mapAiDraftToForm(draft, ingredientUnits));
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateNumberField = (field, value) => {
    if (/^\d*\.?\d*$/.test(value)) {
      updateField(field, value);
    }
  };

  const updateIngredient = (id, field, value) => {
    if (field === "quantity" && !/^\d*\.?\d*$/.test(value)) {
      return;
    }

    setForm((prev) => ({
      ...prev,
      ingredients: prev.ingredients.map((ing) =>
        ing.id === id ? { ...ing, [field]: value } : ing,
      ),
    }));
  };

  const addIngredient = () => {
    setForm((prev) => ({
      ...prev,
      ingredients: [
        {
          id: crypto.randomUUID(),
          name: "",
          quantity: "",
          unit: ingredientUnits?.[0] || "",
        },
        ...prev.ingredients,
      ],
    }));
  };

  const removeIngredient = (id) => {
    setForm((prev) => ({
      ...prev,
      ingredients:
        prev.ingredients.length > 1
          ? prev.ingredients.filter((i) => i.id !== id)
          : prev.ingredients,
    }));
  };

  const toggleMealCategory = (key) => {
    setForm((prev) => ({
      ...prev,
      mealCategories: {
        ...prev.mealCategories,
        [key]: !prev.mealCategories[key],
      },
    }));
  };

  const addTag = (tag) => {
    if (!form.mealTagList.some((t) => t.tagId === tag.tagId)) {
      updateField("mealTagList", [
        ...form.mealTagList,
        { tagId: tag.tagId, tagContent: tag.tagContent },
      ]);
    }
    setShowMealTypeDropdown(false);
  };

  const removeTag = (tagId) => {
    updateField(
      "mealTagList",
      form.mealTagList.filter((t) => t.tagId !== tagId),
    );
  };

  const availableTags = (userMealTags || []).filter(
    (tag) => !form.mealTagList.some((t) => t.tagId === tag.tagId),
  );

  const isFormComplete =
    !!form.mealName?.trim() &&
    form.ingredients.length > 0 &&
    form.ingredients.every(
      (ing) =>
        ing.name?.trim() &&
        ing.quantity?.toString().trim() &&
        ing.unit?.trim(),
    );

  const handleSubmit = (e) => {
    e.preventDefault();

    onSubmit?.(form);
  };

  return (
    <div className="h-full flex flex-col bg-[#fafafa]">
      {/* Header */}
      <div className="flex-shrink-0 z-10">
        <UserMealFormHeader
          heading={heading}
          setShowConfirmCancelModal={confirmCancelClicked}
          onOpenAiFillModal={
            onAiParse ? () => setShowAiFillModal(true) : undefined
          }
        />
      </div>

      {/* Form */}
      <div className="flex-1 overflow-auto p-4 sm:p-8">
        <form ref={formRef} onSubmit={(e) => handleSubmit(e)} className="max-w-3xl">
          <UserMealFormTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            ingredientCount={form.ingredients.length}
          />

          {activeTab === "details" && (
            <>
              <UserMealFormMealName form={form} updateField={updateField} />

              <UserMealFormMealCategory
                form={form}
                toggleMealCategory={toggleMealCategory}
              />

              <UserMealFormMealType
                form={form}
                setShowMealTypeDropdown={setShowMealTypeDropdown}
                showMealTypeDropdown={showMealTypeDropdown}
                availableTags={availableTags}
                addTag={addTag}
                removeTag={removeTag}
              />

              <div className="grid grid-cols-2 gap-6 mb-6">
                <UserMealFormDuration
                  form={form}
                  updateNumberField={updateNumberField}
                />
                <UserMealFormServings
                  form={form}
                  updateNumberField={updateNumberField}
                />
              </div>
            </>
          )}

          {activeTab === "ingredients" && (
            <UserMealFormIngredients
              form={form}
              updateIngredient={updateIngredient}
              addIngredient={addIngredient}
              removeIngredient={removeIngredient}
              ingredientUnits={ingredientUnits}
            />
          )}

        </form>
      </div>

      {/* Sticky footer */}
      <UserMealFormFooter
        confirmButtonText={confirmButtonText}
        setShowConfirmCancelModal={confirmCancelClicked}
        onSubmit={() => formRef.current?.requestSubmit()}
        isFormComplete={isFormComplete}
      />
      {showConfirmCancelModal && (
        <ConfirmActionModal
          action={() => onCancel()}
          closeModal={() => setShowConfirmCancelModal(false)}
          heading="Are you sure?"
          message="Cancelling will discard all changes made to this meal. This action cannot be undone."
          confirmButtonText="Discard Changes"
        />
      )}
      {showAiFillModal && (
        <AiMealFillModal
          onGenerate={handleAiGenerate}
          onClose={() => setShowAiFillModal(false)}
        />
      )}
    </div>
  );
}
